import React from 'react';

interface WaffleChartProps {
  data: Record<string, unknown>[];
  xColumn: string;
  yColumn: string;
  title?: string;
  subtitle?: string;
  colors: string[];
}

interface Segment {
  label: string;
  count: number;
  cells: number;
  color: string;
}

const GRID = 10;
const TOTAL_CELLS = GRID * GRID;
const CELL_SIZE = 32;
const GUTTER = 2;
const GRID_PX = GRID * CELL_SIZE + (GRID - 1) * GUTTER;

function largestRemainderRound(values: number[], target: number): number[] {
  const floored = values.map(v => Math.floor(v));
  const totalFloored = floored.reduce((a, b) => a + b, 0);
  const shortfall = target - totalFloored;
  const result = [...floored];
  const remainders = values
    .map((v, i) => ({ i, rem: v - Math.floor(v) }))
    .sort((a, b) => b.rem - a.rem);
  for (let k = 0; k < shortfall; k++) {
    result[remainders[k].i] += 1;
  }
  return result;
}

function formatCount(n: number): string {
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('en-US');
}

const WaffleChart: React.FC<WaffleChartProps> = ({
  data,
  xColumn,
  yColumn,
  title,
  subtitle,
  colors,
}) => {
  if (!data || data.length === 0) {
    return <div className="chart-empty">No data</div>;
  }
  if (data.length < 2 || data.length > 4) {
    return (
      <div className="chart-error">
        Waffle chart needs 2–4 segments; got {data.length}.
      </div>
    );
  }

  const rawSegments = data.map((row, i) => {
    const rawCount = row[yColumn];
    const count = typeof rawCount === 'number'
      ? rawCount
      : parseFloat(String(rawCount ?? 0));
    const label = String(row[xColumn] ?? `Segment ${i + 1}`);
    return { label, count: Number.isFinite(count) ? count : 0 };
  });

  const total = rawSegments.reduce((s, r) => s + r.count, 0);
  if (total <= 0) {
    return <div className="chart-empty">All segment counts are zero.</div>;
  }

  const rawPcts = rawSegments.map(r => (r.count / total) * 100);
  const cellCounts = largestRemainderRound(rawPcts, TOTAL_CELLS);

  const segments: Segment[] = rawSegments.map((r, i) => ({
    label: r.label,
    count: r.count,
    cells: cellCounts[i],
    color: colors[i % colors.length],
  }));

  const cellColors: string[] = new Array(TOTAL_CELLS);
  let acc = 0;
  segments.forEach(seg => {
    for (let k = 0; k < seg.cells; k++) {
      cellColors[acc++] = seg.color;
    }
  });

  const cells: React.ReactElement[] = [];
  for (let idx = 0; idx < TOTAL_CELLS; idx++) {
    const col = Math.floor(idx / GRID);
    const rowFromBottom = idx % GRID;
    const row = GRID - 1 - rowFromBottom;
    const x = col * (CELL_SIZE + GUTTER);
    const y = row * (CELL_SIZE + GUTTER);
    cells.push(
      <rect
        key={idx}
        x={x}
        y={y}
        width={CELL_SIZE}
        height={CELL_SIZE}
        fill={cellColors[idx]}
        stroke="#1F2937"
        strokeWidth={0.5}
        rx={2}
      />
    );
  }

  return (
    <div className="chart-container" style={{ textAlign: 'center' }}>
      {title && <h3 className="chart-title">{title}</h3>}
      {subtitle && <p className="chart-subtitle">{subtitle}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <svg
          width={GRID_PX}
          height={GRID_PX}
          viewBox={`0 0 ${GRID_PX} ${GRID_PX}`}
          role="img"
          aria-label={`Waffle chart showing ${segments.map(s => s.label).join(', ')}`}
        >
          {cells}
        </svg>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20, fontSize: 13, color: '#9CA3AF' }}>
          {segments.map(seg => {
            const pct = (seg.count / total) * 100;
            return (
              <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    background: seg.color,
                    border: '1px solid #1F2937',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
                <span>
                  <strong style={{ color: '#E5E7EB' }}>{seg.label}</strong>{' '}
                  {pct.toFixed(1)}% ({formatCount(seg.count)})
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 11, color: '#6B7280' }}>
          1 cell = 1% · total {formatCount(total)}
        </div>
      </div>
    </div>
  );
};

export default WaffleChart;
